import { zip } from 'lodash/fp';
import type React from 'react';
import { createContext, Fragment, use, useLayoutEffect, useRef, useState, type FragmentInstance } from 'react';
import breakpoints from '../../breakpoints';
import { pxToNumber } from '../../common/services/number-formatter';

const sizeClasses = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'] as const;

type SizeClass = (typeof sizeClasses)[number];

const fromBreakPoints = (breakPoints: string[]): Record<SizeClass, number> =>
    zip(sizeClasses, breakPoints).reduce(
        (acc, [sizeClass, breakPoint]) => {
            if (!sizeClass) {
                return acc;
            }
            acc[sizeClass] = breakPoint ? pxToNumber(breakPoint) : Infinity;
            return acc;
        },
        {} as Record<SizeClass, number>
    );

// TBD when needed & after renaming contentBreakPoint vars

// const contentAreaBreakpoints = fromBreakPoints([
//     breakpoints.contentBreakPointXXS,
//     breakpoints.contentBreakPointXS,
//     breakpoints.contentBreakPointS,
//     breakpoints.contentBreakPointM,
//     breakpoints.contentBreakPointL,
//     breakpoints.contentBreakPointXL,
// ]);

// const contentAreaContext = createContext<SizeClass | null>(null);

const appAreaBreakpoints = fromBreakPoints([
    breakpoints.breakPointXXS,
    breakpoints.breakPointXS,
    breakpoints.breakPointS,
    breakpoints.breakPointM,
    breakpoints.breakPointL,
    breakpoints.breakPointXL,
]);

const appAreaContext = createContext<SizeClass | null>(null);

const getSizeClassGetter = (areaBreakPoints: Record<SizeClass, number>) => (size: number) =>
    sizeClasses.find(x => size <= areaBreakPoints[x]);

const getDefaultSizeClassGetter = (areaBreakPoints: Record<SizeClass, number>) => () =>
    getSizeClassGetter(areaBreakPoints)(window.innerWidth) ?? 'M';

type SizeProviderProps = {
    areaBreakPoints: Record<SizeClass, number>;
    areaContext: React.Context<SizeClass | null>;
    children: React.ReactNode;
};

function SizeProvider({ children, areaBreakPoints, areaContext }: SizeProviderProps) {
    const [sizeClass, setSizeClass] = useState<SizeClass>(getDefaultSizeClassGetter(areaBreakPoints));
    const ref = useRef<FragmentInstance>(null);

    useLayoutEffect(() => {
        if (!ref.current) {
            return;
        }

        const getSizeClass = getSizeClassGetter(areaBreakPoints);
        const observer = new ResizeObserver(entries => {
            const size = Math.max(...entries.flatMap(entry => entry.borderBoxSize).map(x => x.inlineSize));
            const newSizeClass = getSizeClass(size);
            if (newSizeClass) {
                setSizeClass(newSizeClass);
            }
        });

        ref.current.observeUsing(observer);

        return () => observer.disconnect();
    }, [areaBreakPoints]);

    return (
        <areaContext.Provider value={sizeClass}>
            <Fragment ref={ref}>{children}</Fragment>
        </areaContext.Provider>
    );
}

function useAreaSize(context: React.Context<SizeClass | null>) {
    const size = use(context);

    if (!size) {
        throw new Error('useAreaSize used outside required context');
    }

    return size;
}

function useIsAreaSizeMin(context: React.Context<SizeClass | null>, sizeCompare: SizeClass) {
    const size = useAreaSize(context);
    return sizeClasses.indexOf(size) >= sizeClasses.indexOf(sizeCompare);
}

function useIsAreaSizeMax(context: React.Context<SizeClass | null>, sizeCompare: SizeClass) {
    const size = useAreaSize(context);
    return sizeClasses.indexOf(size) <= sizeClasses.indexOf(sizeCompare);
}

function useIsAreaSizeRange(context: React.Context<SizeClass | null>, sizeFrom: SizeClass, sizeUntil: SizeClass) {
    const size = useAreaSize(context);
    const sizeIndex = sizeClasses.indexOf(size);
    return sizeIndex >= sizeClasses.indexOf(sizeFrom) && sizeIndex <= sizeClasses.indexOf(sizeUntil);
}

function useAreaResponsiveValue<T>(
    context: React.Context<SizeClass | null>,
    sizeMap: Partial<Record<SizeClass, T>>,
    defaultVal: T
) {
    const currentSize = useAreaSize(context);
    const sizesToCheck = sizeClasses.slice(sizeClasses.indexOf(currentSize));

    for (const size of sizesToCheck) {
        if (sizeMap[size] !== undefined) {
            return sizeMap[size];
        }
    }

    return defaultVal;
}

export const AppAreaSizeProvider = ({ children }: { children: React.ReactNode }) => (
    <SizeProvider areaBreakPoints={appAreaBreakpoints} areaContext={appAreaContext}>
        {children}
    </SizeProvider>
);

export const useAppAreaSize = () => useAreaSize(appAreaContext);
export const useIsAppAreaSizeMin = (sizeCompare: SizeClass) => useIsAreaSizeMin(appAreaContext, sizeCompare);
export const useIsAppAreaSizeMax = (sizeCompare: SizeClass) => useIsAreaSizeMax(appAreaContext, sizeCompare);
export const useIsAppAreaSizeRange = (sizeFrom: SizeClass, sizeUntil: SizeClass) =>
    useIsAreaSizeRange(appAreaContext, sizeFrom, sizeUntil);
export function useAppAreaResponsiveValue<T>(sizeMap: Partial<Record<SizeClass, T>>, defaultVal: T) {
    return useAreaResponsiveValue(appAreaContext, sizeMap, defaultVal);
}
